import { useIonToast } from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IState, ITabProperties } from 'appRedux/createStore';
import { CasinoService, IRequestCasinoParams, IRequestDetailParam } from 'services/casino';
import { debounce } from 'lodash';
import { convertToSlotData, FilterTypeEnum, IFilterType, OrderByEnum } from 'common/common';
import { config } from 'config/config';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import SlotStatsCard from './components/Detail/SlotStatsCard';
import { FilterReduxCommandCreator } from 'appRedux/actions/filterCommandCreator';
import { TimeFrameValue } from 'common/types';
import TrendsComponent from './components/Detail/TrendsComponent';
import { ICasinoReport } from 'appRedux/models/casinoModels';
import GraphComponent from './components/Detail/GraphComponent';
import GameInformation from './components/Detail/GameInformation';
import SVGComponent from 'components/SmartLoading/SVGComponent';
import UserMessages from 'components/UserMessages/UserMessages';
import { AuthService, IUserMessage } from 'services/auth';
import { withInsightRtp } from 'services/insightCasino';
import { parseValue } from 'common/include';
import {
  playerScopedKey,
  sharedPlayerScopedGet,
  userMessagesListBucket,
} from 'lib/playerScopedListCache';
import { getEgmDetailGraphDefaultTimeframe } from 'lib/applicationConfig';

export interface ISelectedSlots {
  slotNumber: number;
  slotData: ICasinoReport;
}

interface IDetailReport {
  onBackSlotList: () => void;
  setSelectedSlotAction: (selectedData: ICasinoReport | null) => void;
  onStatsRefChange: (refValue: HTMLDivElement | null) => void;
}

const DetailReport = ({
  onBackSlotList,
  setSelectedSlotAction,
  onStatsRefChange,
}: IDetailReport) => {
  const casinoSvc = new CasinoService();
  const dispatch = useDispatch();
  const filterCommandCreator = FilterReduxCommandCreator(dispatch);

  const [isSlotPlayDataIsLoading, setSlotPlayDataIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [presentToast] = useIonToast();
  const reportType = FilterTypeEnum.snapfilter;
  const reportGraphType = FilterTypeEnum.graphfilter;

  const tabFilters = useSelector<IState>(
    (state) => state.app.persistedState.filterState[reportType as IFilterType]
  ) as ITabProperties;

  const { timeframe: selectedTimeFrame } = tabFilters;
  const { activeSlotNumber, clearSlotHistory } = useSlotDetailHistory();

  const [activeRange, setActiveRange] = useState<TimeFrameValue>(selectedTimeFrame);
  // applicationConfigurations egmDetailGraphDefaultTimeframe; fallback months1.
  // Session changes via setGraphActiveRange still work.
  const [activeGraphRange, setGraphActiveRange] = useState<TimeFrameValue>(
    () => getEgmDetailGraphDefaultTimeframe() as TimeFrameValue
  );

  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;

  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;

  // Subscribe so SlotHeader displayId re-resolves when GetSearchData catalog arrives.
  useSelector<IState>((state) => state.app.persistedState.casinoSearchData);

  const appUserPk = useSelector<IState>((state) => state.app.persistedState.userInfo?.appUserPk);
  const authSvc = useMemo(() => new AuthService(), []);

  const [currentMessage, setCurrentMessage] = useState<IUserMessage | null>(null);
  const [userMessagesVisible, setUserMessagesVisible] = useState<boolean>(false);

  useEffect(() => {
    let sub: any = null;
    if (casinoId) {
      const msgKey = playerScopedKey(String(appUserPk || 'anon'), casinoId);
      sub = sharedPlayerScopedGet(userMessagesListBucket, msgKey, () =>
        authSvc.GetUserMessages(appUserPk as string | undefined, casinoId)
      ).subscribe((resp: any) => {
        const list = resp && resp.DataSet ? (resp.DataSet as IUserMessage[]) : [];
        if (list.length > 0) {
          const m = list[0];
          setCurrentMessage(m);
          setUserMessagesVisible(true);
        }
      });
    }
    return () => {
      if (sub && sub.unsubscribe) sub.unsubscribe();
    };
  }, [authSvc, casinoId, appUserPk]);

  const handleUserMessagesClose = useCallback(() => {
    const msg = currentMessage;
    if (appUserPk && msg) {
      authSvc.UpdateUserMessageShownDate(msg._id).subscribe?.();
    }
    setUserMessagesVisible(false);
  }, [appUserPk, authSvc, currentMessage]);

  const [selectedSlot, setSelectedSlot] = useState<ISelectedSlots | null>(null);
  const [casinoParams, setCasinoParams] = useState<IRequestDetailParam>({
    casinofk: casinoId,
    insight: 'spins',
    slot: 0,
    orderby: OrderByEnum.Desc,
  });

  const handleInputChange = (param: keyof IRequestCasinoParams, value: string | number) => {
    setCasinoParams((prevParams) => ({ ...prevParams, [param]: value }));
  };

  const fetchIndividualSlotReport = useCallback((queryParams: IRequestCasinoParams) => {
    try {
      setSelectedSlot(null);
      setSelectedSlotAction(null);
      setSlotPlayDataIsLoading(true);
      const { ...restParam } = queryParams;
      casinoSvc.GetIndividualSlotReport(restParam).subscribe({
        next(casinoResponse) {
          if (casinoResponse) {
            casinoResponse.DataSet &&
              setSelectedSlot({
                slotNumber: activeSlotNumber,
                slotData: casinoResponse.DataSet[0],
              });
            casinoResponse.DataSet && setSelectedSlotAction(casinoResponse.DataSet[0]);
          }
        },
        error(_err) {
          presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Failed to retrieve casino report.',
          }).catch(() => null);
          setSlotPlayDataIsLoading(false);
          setIsError(true);
        },
        complete() {
          setSlotPlayDataIsLoading(false);
        },
      });
    } catch (error) {}
  }, []);

  const debouncedGetSlotDataReport = useMemo(
    () =>
      debounce((casinoParams) => {
        fetchIndividualSlotReport(casinoParams);
      }, 500),
    [fetchIndividualSlotReport]
  );

  useEffect(() => {
    debouncedGetSlotDataReport(casinoParams);
    return () => {
      debouncedGetSlotDataReport.cancel();
    };
  }, [casinoParams, debouncedGetSlotDataReport]);

  const handleTimeRangeChange = (value: TimeFrameValue) => {
    filterCommandCreator.SetFilterOptions(reportType as FilterTypeEnum, {
      timeframe: value,
    });
    setActiveRange(value);
  };

  const handleTimeRangeGraphChange = (value: TimeFrameValue) => {
    filterCommandCreator.SetFilterOptions(reportGraphType as FilterTypeEnum, {
      timeframe: value,
    });
    setGraphActiveRange(value);
  };

  useEffect(() => {
    const filterTime = config.allTimeFrames.filter(
      (timeframe) => timeframe.value === selectedTimeFrame
    )[0];
    handleInputChange('timeframe', filterTime.id);
  }, [selectedTimeFrame]);

  useEffect(() => {
    setSelectedSlot(null);
    setSelectedSlotAction(null);
    handleInputChange('slot', activeSlotNumber);
  }, [activeSlotNumber]);

  useEffect(() => {
    let timer: number | undefined;
    if (userMessagesVisible) {
      timer = window.setTimeout(() => {
        handleUserMessagesClose();
      }, 10000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [userMessagesVisible, handleUserMessagesClose]);
  const handleBack = () => {
    clearSlotHistory();
    onBackSlotList();
  };

  return (
    <div className="parent-detail">
      {isSlotPlayDataIsLoading && (
        <div className="spinner-loading">
          <div style={{ position: 'relative', width: '100%', height: 100 }}>
            <SVGComponent />
          </div>
        </div>
      )}
      {userMessagesVisible && currentMessage && (
        <UserMessages
          title={currentMessage.title}
          description={currentMessage.description}
          visible={userMessagesVisible}
          onClose={handleUserMessagesClose}
          className="m-user-messages__detail"
        />
      )}
      {selectedSlot && !isSlotPlayDataIsLoading && (
        <>
          <SlotStatsCard
            slotData={withInsightRtp(convertToSlotData(selectedSlot.slotData), selectedSlot.slotData, parseValue)}
            activeRange={activeRange}
            isProUser={isProUser}
            onRefChange={onStatsRefChange}
            onTimeRangeChange={(range) => {
              handleTimeRangeChange(range);
            }}
            onBack={handleBack}
          />
          <TrendsComponent slotNumber={selectedSlot.slotNumber} />
          <GraphComponent
            isProUser={isProUser}
            activeRange={activeGraphRange}
            playData={selectedSlot.slotData}
            onClickUpgradeNow={() => {
              console.log('Click Upgrade');
            }}
            onTimeRangeChange={(range) => {
              handleTimeRangeGraphChange(range);
            }}
          />
          <GameInformation casinoReport={selectedSlot.slotData} />
        </>
      )}
    </div>
  );
};

export default DetailReport;
